<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <!-- HEADER -->
    <header class="bg-[#1a1a1a] text-white shadow">
      <div class="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <router-link to="/">
          <img src="/logo.png" alt="Logo" class="h-12 w-auto" />
        </router-link>
        <router-link
          to="/"
          class="text-sm text-white/80 hover:text-orange-400 transition"
        >
          Voltar para Início
        </router-link>
      </div>
    </header>

    <!-- CONTEÚDO -->
    <main class="flex-1">
      <section v-if="ad" class="max-w-6xl mx-auto px-4 py-10">
        <div
          class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
        >
          <!-- IMAGEM PRINCIPAL -->
          <div class="w-full h-[320px] sm:h-[420px] bg-gray-100">
            <img
              v-if="ad.images && ad.images.length"
              :src="ad.images[0]"
              alt="Imagem do anúncio"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-gray-400 italic"
            >
              Sem imagem disponível
            </div>
          </div>

          <!-- CONTEÚDO PRINCIPAL -->
          <div class="p-6 sm:p-10">
            <h1 class="text-2xl sm:text-3xl font-bold text-primary mb-3">
              {{ ad.title }}
            </h1>

            <div class="flex flex-wrap items-center gap-3 mb-5 text-gray-600">
              <span
                class="inline-block bg-[#f6f4f1] border border-gray-200 px-3 py-1 rounded-full text-sm"
              >
                {{ ad.category?.description || "Sem categoria" }}
              </span>
              <span class="text-sm flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {{ ad.city }} - {{ ad.state }}
              </span>
            </div>

            <!-- DESCRIÇÃO -->
            <p class="text-gray-700 leading-relaxed mb-8 whitespace-pre-line">
              {{ ad.description || "Nenhuma descrição disponível." }}
            </p>

            <!-- INFORMAÇÕES DA EMPRESA -->
            <div
              class="bg-[#f9f9f9] border border-gray-200 rounded-xl p-5 sm:p-6 mb-6"
            >
              <h2 class="text-xl font-semibold text-primary mb-3">
                Sobre a Empresa
              </h2>

              <p class="text-gray-700 mb-2">
                <span class="font-medium">Nome:</span>
                {{ ad.client?.name || "Não informado" }}
              </p>
              <p class="text-gray-700 mb-2">
                <span class="font-medium">E-mail:</span>
                {{ ad.client?.email || "Não informado" }}
              </p>
              <p class="text-gray-700 mb-4">
                <span class="font-medium">Telefone:</span>
                {{ ad.client?.phone || "Não informado" }}
              </p>

              <!-- BOTÃO WHATSAPP -->
              <div class="flex justify-center sm:justify-start">
                <a
                  v-if="ad.client?.phone"
                  :href="`https://wa.me/55${sanitizePhone(ad.client.phone)}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 bg-[#25D366] text-white px-5 py-2 rounded-full shadow hover:bg-[#1EBE5A] transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style="width: 22px; height: 22px"
                  >
                    <path
                      d="M12.04 2C6.5 2 2 6.5 2 12.04c0 1.84.48 3.62 1.39 5.2L2 22l4.9-1.28A9.97 9.97 0 0 0 12.04 22C17.55 22 22 17.55 22 12.04S17.55 2 12.04 2zm0 18.21a8.2 8.2 0 0 1-4.17-1.14l-.3-.18-2.48.64.66-2.57-.17-.27A8.13 8.13 0 0 1 3.84 12c0-4.54 3.68-8.21 8.2-8.21 2.19 0 4.25.86 5.8 2.42a8.16 8.16 0 0 1 2.4 5.79c0 4.53-3.68 8.21-8.2 8.21zm4.56-6.17c-.25-.12-1.47-.72-1.7-.8-.23-.08-.4-.12-.57.12-.17.25-.65.8-.8.96-.15.17-.3.19-.55.08-.25-.12-1.06-.39-2.02-1.24-.74-.66-1.24-1.48-1.38-1.73-.14-.25-.01-.39.11-.52.12-.12.25-.3.37-.45.12-.15.16-.25.25-.42.08-.17.04-.32-.02-.45-.06-.12-.57-1.37-.78-1.88-.21-.5-.43-.43-.57-.44h-.48c-.17 0-.44.06-.67.32-.23.25-.88.86-.88 2.09 0 1.22.9 2.4 1.03 2.56.13.17 1.78 2.71 4.32 3.8.6.26 1.06.42 1.42.54.6.19 1.14.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.17-.47-.29z"
                    />
                  </svg>
                  <span class="font-semibold">Conversar no WhatsApp</span>
                </a>
              </div>
            </div>

            <!-- METADADOS -->
            <div class="text-xs text-gray-500 text-right">
              Publicado em:
              {{ formatDate(ad.created_at) }}
            </div>
          </div>
        </div>
      </section>

      <!-- LOADING / ERRO -->
      <div v-else class="flex justify-center py-20">
        <p class="text-gray-600 text-lg">Carregando anúncio...</p>
      </div>
    </main>

    <!-- FOOTER -->
    <AppFooter :enterprise="enterpriseData" />
    <Copyright :enterprise="enterpriseData" />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import AppFooter from "@/components/AppFooter.vue";
import Copyright from "@/components/Copyright.vue";

const route = useRoute();
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE;
const ad = ref(null);
const enterpriseData = ref({});

function sanitizePhone(phone) {
  return phone ? phone.replace(/\D/g, "") : "";
}

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

async function fetchAdDetails() {
  try {
    const slug = route.params.slug;
    const response = await api.get(`/advertisements/slug/${slug}`);
    const data = response.data.data;

    data.images = JSON.parse(data.images || "[]").map(
      (img) => `${app_url_api_storage}/images/${img}`
    );

    ad.value = data;
  } catch (error) {
    console.error("Erro ao buscar anúncio:", error);
  }
}

async function fetchEnterpriseData() {
  try {
    const res = await api.get("/enterprise/home");
    enterpriseData.value = res.data.data[0] || {};
  } catch (error) {
    console.error("Erro ao buscar dados da empresa:", error);
  }
}

onMounted(async () => {
  await fetchAdDetails();
  await fetchEnterpriseData();
});
</script>

<style scoped>
.text-primary {
  color: #152a34;
}

.bg-primary {
  background-color: #152a34;
}

@media (max-width: 640px) {
  h1 {
    font-size: 1.5rem;
  }
}
</style>
